import { useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, confirmTotpEnrollment, enrollTotp, login, verifyTotp } from '../api';
import { translateErrorCode, type Locale, type Translate } from '../i18n';
import { Button, Notice, TextInput } from './kit';
import {
  IconCheck,
  IconDrop,
  IconEye,
  IconEyeOff,
  IconLock,
  IconShield,
  IconUser,
  type IconProps,
} from './icons';

export interface LoginViewProps {
  readonly t: Translate;
  readonly locale: Locale;
  readonly onSignedIn: () => void;
}

/**
 * Поле с глифом слева.
 *
 * Иконка здесь не украшение: на экране входа два поля подряд, и по глифу «человек»
 * и «замок» строка узнаётся раньше, чем прочитана подпись, — особенно когда
 * заходят десятый раз за день и смотрят на экран вполглаза. Геометрия и цвета —
 * те же, что у остальных полей интерфейса: отличается только левый отступ.
 */
function IconField({
  label,
  glyph: Glyph,
  trailing,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  glyph: (p: IconProps) => React.JSX.Element;
  trailing?: ReactNode;
}) {
  const id = `login-${props.name ?? label}`;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <div className="relative flex items-center">
        <Glyph
          size={17}
          className="pointer-events-none absolute left-3 text-ink-faint"
          aria-hidden
        />
        <TextInput {...props} id={id} className="h-10 pl-10 text-[15px]" />
        {trailing}
      </div>
    </div>
  );
}

function HeroPoint({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-hero-ink/15">
        <IconCheck size={12} />
      </span>
      <span className="text-sm leading-relaxed opacity-90">{children}</span>
    </li>
  );
}

export function LoginView({ t, locale, onSignedIn }: LoginViewProps) {
  const [stage, setStage] = useState<'password' | 'totp' | 'enroll'>('password');
  const [enrollment, setEnrollment] = useState<{ secret: string; uri: string } | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  /**
   * Общий словарь кодов на «неверный пароль» отвечает «Требуется вход в систему»
   * — на экране входа это бессмыслица: человек и так входит. Подменяем только
   * здесь и только 401: сам код нужен серверу и остальным экранам как есть.
   * Про то, логин неверен или пароль, не уточняем намеренно — иначе форма входа
   * превращается в способ проверять, существует ли такой сотрудник.
   */
  async function run(action: () => Promise<void>, denied: string): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      await action();
    } catch (err) {
      if (err instanceof ApiError) {
        const deniedCode = err.code === 'E00.unauthorized' || err.code === 'E00.login_failed';
        setError(deniedCode ? denied : translateErrorCode(locale, err.code));
      } else {
        setError(String((err as Error).message));
      }
    } finally {
      setBusy(false);
    }
  }

  const submitPassword = (): Promise<void> =>
    run(async () => {
      const result = await login(name.trim(), password);
      if (result.needsTotpEnrollment) {
        // Секрета ещё нет: сотрудника завели, а фактор он не подключал. Без
        // этого шага административная роль в админку не попадала вовсе.
        setEnrollment(await enrollTotp());
        setStage('enroll');
      } else if (result.needsTotp) setStage('totp');
      else onSignedIn();
    }, t('shell.login.failed'));

  const submitTotp = (): Promise<void> =>
    run(async () => {
      if (stage === 'enroll') await confirmTotpEnrollment(code.trim());
      else await verifyTotp(code.trim());
      onSignedIn();
    }, t('shell.totp.failed'));

  const totp = stage === 'totp' || stage === 'enroll';

  return (
    <div className="grid h-full overflow-y-auto lg:grid-cols-[1.05fr_.95fr]">
      {/*
       * Левая панель говорит, куда человек попал и зачем эта система нужна, — на
       * узком экране она не сжимается в полоску, а убирается: там дорог каждый
       * пиксель под форму, а рассказ о продукте на телефоне никто не читает.
       */}
      <aside
        className="relative hidden flex-col justify-between overflow-hidden p-12 text-hero-ink lg:flex"
        style={{
          background:
            'linear-gradient(155deg, var(--color-hero-from) 0%, var(--color-hero-to) 100%)',
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 -right-32 h-[28rem] w-[28rem] rounded-full bg-hero-ink/10 blur-2xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-36 -left-28 h-96 w-96 rounded-full bg-hero-ink/10 blur-2xl"
        />
        <IconDrop
          aria-hidden
          size={520}
          className="pointer-events-none absolute -right-24 -bottom-32 opacity-[0.07]"
        />

        <div className="relative flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-panel bg-hero-ink/15">
            <IconDrop size={24} />
          </span>
          <div className="min-w-0">
            <p className="text-base font-semibold tracking-tight">{t('shell.title')}</p>
            <p className="text-xs opacity-75">{t('shell.hero.tagline')}</p>
          </div>
        </div>

        <div className="relative max-w-md">
          <h2 className="text-3xl leading-tight font-semibold tracking-tight text-balance">
            {t('shell.hero.title')}
          </h2>
          <p className="mt-3 text-sm leading-relaxed opacity-85">{t('shell.hero.text')}</p>
        </div>

        <ul className="relative flex max-w-md flex-col gap-3">
          <HeroPoint>{t('shell.hero.f1')}</HeroPoint>
          <HeroPoint>{t('shell.hero.f2')}</HeroPoint>
          <HeroPoint>{t('shell.hero.f3')}</HeroPoint>
        </ul>
      </aside>

      <main className="flex items-center justify-center bg-canvas px-4 py-10 sm:px-8">
        <form
          className="w-full max-w-sm"
          onSubmit={(event) => {
            event.preventDefault();
            void (totp ? submitTotp() : submitPassword());
          }}
        >
          <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-panel bg-brand-600 text-ink-invert shadow-card">
            {totp ? <IconShield size={24} /> : <IconDrop size={24} />}
          </span>

          <h1 className="text-2xl font-semibold tracking-tight text-ink">
            {stage === 'enroll'
              ? t('shell.enroll.title')
              : totp
                ? t('shell.totp.title')
                : t('shell.login.welcome')}
          </h1>
          <p className="mt-1.5 mb-7 text-sm text-ink-muted">
            {stage === 'enroll' ? t('shell.enroll.sub') : totp ? t('shell.totp.sub') : t('shell.login.sub')}
          </p>

          <div className="flex flex-col gap-4">
            {stage === 'enroll' && enrollment ? (
              <div className="flex flex-col gap-2 rounded-panel border border-line bg-sunken p-3 text-sm">
                <p className="text-ink-muted">{t('shell.enroll.step')}</p>
                <p className="text-xs font-medium text-ink-muted">{t('shell.enroll.key')}</p>
                <code className="break-all font-mono text-base tracking-wider text-ink select-all">
                  {enrollment.secret.replace(/(.{4})/g, '$1 ').trim()}
                </code>
                <a href={enrollment.uri} className="font-medium text-brand-700 hover:underline">
                  {t('shell.enroll.link')}
                </a>
              </div>
            ) : null}
            {totp ? (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="login-code" className="text-sm font-medium text-ink">
                  {t('shell.totp.hint')}
                </label>
                <TextInput
                  id="login-code"
                  className="h-12 text-center text-xl tracking-[0.4em] tabular"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
              </div>
            ) : (
              <>
                <IconField
                  label={t('shell.login.name')}
                  glyph={IconUser}
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <IconField
                  label={t('shell.login.password')}
                  glyph={IconLock}
                  name="password"
                  type={reveal ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  trailing={
                    <button
                      type="button"
                      onClick={() => setReveal((v) => !v)}
                      title={reveal ? t('shell.login.hidePassword') : t('shell.login.showPassword')}
                      aria-label={
                        reveal ? t('shell.login.hidePassword') : t('shell.login.showPassword')
                      }
                      className="absolute right-1.5 flex h-8 w-8 items-center justify-center rounded-control text-ink-faint transition hover:bg-sunken hover:text-ink"
                    >
                      {reveal ? <IconEyeOff size={17} /> : <IconEye size={17} />}
                    </button>
                  }
                />
              </>
            )}

            {error ? <Notice tone="error">{error}</Notice> : null}

            <Button type="submit" tone="primary" disabled={busy} className="mt-1 h-10 w-full">
              {totp ? t('shell.totp.submit') : t('shell.login.submit')}
            </Button>

            {totp ? (
              <button
                type="button"
                onClick={() => {
                  setStage('password');
                  setEnrollment(null);
                  setCode('');
                  setError(undefined);
                }}
                className="text-sm font-medium text-brand-700 transition hover:underline"
              >
                {t('shell.login.back')}
              </button>
            ) : null}
          </div>

          <p className="mt-8 text-center text-xs text-ink-faint">{t('shell.login.foot')}</p>
        </form>
      </main>
    </div>
  );
}
